/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — export-excel.ts
   exportToExcel(), buildISData(), buildBSData(), buildCFData(),
   buildEquityData(), cell formatting
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { sum, rows, meta, hasPriorData, computeNetIncome, extractYear } from './utils';
import { trackEvent } from './config';
import {
  mergeLabels,
  findNoncashItems,
  computeWorkingCapitalChanges,
  detectInvestingActivities,
  detectFinancingActivities,
  type CashFlowLineItem,
} from './statements';

// ─── External library declarations ────────────────────────────────────────
declare const XLSX: any;

/**
 * Exports all financial statements to a multi-sheet Excel workbook (.xlsx) and triggers download.
 */
export function exportToExcel(): void {
  try {
  trackEvent('excel_exports');
  const m = meta();
  const multi = hasPriorData();
  const wb = XLSX.utils.book_new();

  // Period formatting helpers
  const curYear: string = (m.period ? extractYear(m.period) : null) || 'Current';
  const priorYear: string = (m.priorPeriod ? extractYear(m.priorPeriod) : null) || 'Prior';
  const priorYearOnly: string = (m.priorPeriod ? extractYear(m.priorPeriod) : null) || m.priorPeriod || '';
  function bsPeriod(): string {
    if (multi && m.priorPeriod) return m.period + ' and ' + priorYearOnly;
    return m.period;
  }
  function flowPeriod(): string {
    const base = multi && m.priorPeriod ? m.period + ' and ' + priorYearOnly : m.period;
    return base && base !== 'Period' ? 'For the Year Ended ' + base : base;
  }

  function buildISData(): any[][] {
    const aoa: any[][] = [];
    aoa.push([m.company]);
    aoa.push(['Income Statement']);
    aoa.push([flowPeriod()]);
    aoa.push([]);

    if (multi) {
      aoa.push(['', curYear, priorYear]);
    } else {
      aoa.push(['', 'Amount']);
    }

    const totalRevenue = sum(state.currentData, 'revenue');
    const totalCOGS = sum(state.currentData, 'cogs');
    const grossProfit = totalRevenue - totalCOGS;
    const totalOpex = sum(state.currentData, 'opex');
    const operatingIncome = grossProfit - totalOpex;
    const totalOther = sum(state.currentData, 'other');
    const netIncome = computeNetIncome(state.currentData);

    let pTotalRevenue = 0, pTotalCOGS = 0, pGrossProfit = 0, pTotalOpex = 0, pOperatingIncome = 0, pTotalOther = 0, pNetIncome = 0;
    if (multi) {
      pTotalRevenue = sum(state.priorData, 'revenue');
      pTotalCOGS = sum(state.priorData, 'cogs');
      pGrossProfit = pTotalRevenue - pTotalCOGS;
      pTotalOpex = sum(state.priorData, 'opex');
      pOperatingIncome = pGrossProfit - pTotalOpex;
      pTotalOther = sum(state.priorData, 'other');
      pNetIncome = computeNetIncome(state.priorData);
    }

    function addSection(sectionLabel: string, sectionKey: string): void {
      aoa.push([sectionLabel]);
      const items = mergeLabels(rows(state.currentData, sectionKey), multi ? rows(state.priorData, sectionKey) : [], multi);
      items.forEach(function (item) {
        if (multi) {
          aoa.push(['  ' + item.label, item.cur, item.prior]);
        } else {
          aoa.push(['  ' + item.label, item.cur]);
        }
      });
    }

    addSection('Revenue', 'revenue');
    if (multi) { aoa.push(['Total Revenue', totalRevenue, pTotalRevenue]); }
    else { aoa.push(['Total Revenue', totalRevenue]); }

    addSection('Cost of Goods Sold', 'cogs');
    if (multi) { aoa.push(['Total Cost of Goods Sold', totalCOGS, pTotalCOGS]); }
    else { aoa.push(['Total Cost of Goods Sold', totalCOGS]); }

    if (multi) { aoa.push(['Gross Profit', grossProfit, pGrossProfit]); }
    else { aoa.push(['Gross Profit', grossProfit]); }

    addSection('Operating Expenses', 'opex');
    if (multi) { aoa.push(['Total Operating Expenses', totalOpex, pTotalOpex]); }
    else { aoa.push(['Total Operating Expenses', totalOpex]); }

    if (multi) { aoa.push(['Operating Income', operatingIncome, pOperatingIncome]); }
    else { aoa.push(['Operating Income', operatingIncome]); }

    if (rows(state.currentData, 'other').length || (multi && rows(state.priorData, 'other').length)) {
      addSection('Other Income / (Expense)', 'other');
      if (multi) { aoa.push(['Total Other Income / (Expense)', totalOther, pTotalOther]); }
      else { aoa.push(['Total Other Income / (Expense)', totalOther]); }
    }

    if (multi) { aoa.push(['Net Income', netIncome, pNetIncome]); }
    else { aoa.push(['Net Income', netIncome]); }

    return aoa;
  }

  function buildBSData(): any[][] {
    const aoa: any[][] = [];
    aoa.push([m.company]);
    aoa.push(['Balance Sheet']);
    aoa.push([bsPeriod()]);
    aoa.push([]);

    if (multi) {
      aoa.push(['', curYear, priorYear]);
    } else {
      aoa.push(['', 'Amount']);
    }

    const totalCA = sum(state.currentData, 'current-assets');
    const totalNCA = sum(state.currentData, 'noncurrent-assets');
    const totalAssets = totalCA + totalNCA;
    const totalCL = sum(state.currentData, 'current-liab');
    const totalNCL = sum(state.currentData, 'noncurrent-liab');
    const totalLiab = totalCL + totalNCL;
    const bsNetIncome = computeNetIncome(state.currentData);
    const totalEquity = sum(state.currentData, 'equity') + bsNetIncome;
    const totalLiabEquity = totalLiab + totalEquity;

    let pTotalCA = 0, pTotalNCA = 0, pTotalAssets = 0, pTotalCL = 0, pTotalNCL = 0, pTotalLiab = 0, pTotalEquity = 0, pTotalLiabEquity = 0;
    let pBsNetIncome = 0;
    if (multi) {
      pTotalCA = sum(state.priorData, 'current-assets');
      pTotalNCA = sum(state.priorData, 'noncurrent-assets');
      pTotalAssets = pTotalCA + pTotalNCA;
      pTotalCL = sum(state.priorData, 'current-liab');
      pTotalNCL = sum(state.priorData, 'noncurrent-liab');
      pTotalLiab = pTotalCL + pTotalNCL;
      pBsNetIncome = computeNetIncome(state.priorData);
      pTotalEquity = sum(state.priorData, 'equity') + pBsNetIncome;
      pTotalLiabEquity = pTotalLiab + pTotalEquity;
    }

    function addSection(sectionLabel: string, sectionKey: string): void {
      aoa.push([sectionLabel]);
      const items = mergeLabels(rows(state.currentData, sectionKey), multi ? rows(state.priorData, sectionKey) : [], multi);
      items.forEach(function (item) {
        if (multi) { aoa.push(['  ' + item.label, item.cur, item.prior]); }
        else { aoa.push(['  ' + item.label, item.cur]); }
      });
    }

    function addTotal(label: string, curVal: number, prior: number): void {
      if (multi) { aoa.push([label, curVal, prior]); }
      else { aoa.push([label, curVal]); }
    }

    aoa.push(['ASSETS']);
    addSection('Current Assets', 'current-assets');
    addTotal('Total Current Assets', totalCA, pTotalCA);

    addSection('Non-Current Assets', 'noncurrent-assets');
    addTotal('Total Non-Current Assets', totalNCA, pTotalNCA);
    addTotal('TOTAL ASSETS', totalAssets, pTotalAssets);

    aoa.push(['LIABILITIES & EQUITY']);
    addSection('Current Liabilities', 'current-liab');
    addTotal('Total Current Liabilities', totalCL, pTotalCL);

    addSection('Non-Current Liabilities', 'noncurrent-liab');
    addTotal('Total Non-Current Liabilities', totalNCL, pTotalNCL);
    addTotal('Total Liabilities', totalLiab, pTotalLiab);

    addSection("Shareholders' Equity", 'equity');
    if (multi) { aoa.push(['  Net Income', bsNetIncome, pBsNetIncome]); }
    else { aoa.push(['  Net Income', bsNetIncome]); }
    addTotal('Total Equity', totalEquity, pTotalEquity);
    addTotal('TOTAL LIABILITIES & EQUITY', totalLiabEquity, pTotalLiabEquity);

    return aoa;
  }

  function buildCFData(): any[][] {
    const aoa: any[][] = [];
    aoa.push([m.company]);
    aoa.push(['Statement of Cash Flows']);
    aoa.push([flowPeriod()]);
    aoa.push([]);

    if (multi) {
      aoa.push(['', curYear, priorYear]);
    } else {
      aoa.push(['', 'Amount']);
    }

    function addRow(label: string, curVal: number, prior: number, indent?: boolean): void {
      const prefix = indent ? '  ' : '';
      if (multi) { aoa.push([prefix + label, curVal, prior]); }
      else { aoa.push([prefix + label, curVal]); }
    }

    const netIncome = computeNetIncome(state.currentData);
    const pNetIncome = multi ? computeNetIncome(state.priorData) : 0;
    const manualOpLabels = rows(state.currentData, 'cf-operating').map(function (r) { return r.label; });
    const pManualOpLabels = multi ? rows(state.priorData, 'cf-operating').map(function (r) { return r.label; }) : [];
    const noncashItems = findNoncashItems(state.currentData, manualOpLabels);
    const pNoncashItems = multi ? findNoncashItems(state.priorData, pManualOpLabels) : [];
    const wcChanges = multi ? computeWorkingCapitalChanges(state.currentData, state.priorData) : [];
    const autoNoncashTotal = noncashItems.reduce(function (s, i) { return s + i.amount; }, 0);
    const pAutoNoncashTotal = pNoncashItems.reduce(function (s, i) { return s + i.amount; }, 0);
    const wcTotal = wcChanges.reduce(function (s, i) { return s + i.amount; }, 0);
    const manualOpTotal = sum(state.currentData, 'cf-operating');
    const pManualOpTotal = multi ? sum(state.priorData, 'cf-operating') : 0;
    const totalOp = netIncome + autoNoncashTotal + wcTotal + manualOpTotal;
    const pTotalOp = multi ? (pNetIncome + pAutoNoncashTotal + pManualOpTotal) : 0;
    // Auto-detect investing/financing activities
    const autoInvItems: CashFlowLineItem[] = multi
      ? detectInvestingActivities(state.currentData, state.priorData) : [];
    const autoFinItems: CashFlowLineItem[] = multi
      ? detectFinancingActivities(state.currentData, state.priorData) : [];
    const manualInvItems = mergeLabels(rows(state.currentData, 'cf-investing'), multi ? rows(state.priorData, 'cf-investing') : [], multi);
    const manualFinItems = mergeLabels(rows(state.currentData, 'cf-financing'), multi ? rows(state.priorData, 'cf-financing') : [], multi);
    const autoInvTotal = autoInvItems.reduce(function (s, i) { return s + i.amount; }, 0);
    const autoFinTotal = autoFinItems.reduce(function (s, i) { return s + i.amount; }, 0);
    const manualInvTotal = sum(state.currentData, 'cf-investing');
    const manualFinTotal = sum(state.currentData, 'cf-financing');
    const pManualInvTotal = multi ? sum(state.priorData, 'cf-investing') : 0;
    const pManualFinTotal = multi ? sum(state.priorData, 'cf-financing') : 0;
    const totalInv = autoInvTotal + manualInvTotal;
    const totalFin = autoFinTotal + manualFinTotal;
    const pTotalInv = pManualInvTotal;
    const pTotalFin = pManualFinTotal;
    const netChange = totalOp + totalInv + totalFin;
    const pNetChange = multi ? (pTotalOp + pTotalInv + pTotalFin) : 0;

    aoa.push(['Cash Flows from Operating Activities']);
    addRow('Net Income', netIncome, pNetIncome, true);
    if (noncashItems.length > 0 || (multi && pNoncashItems.length > 0)) {
      aoa.push(['  Adjustments for non-cash items:']);
      if (multi) {
        const allNc = mergeLabels(
          noncashItems.map(function (i) { return { label: i.label, amount: i.amount }; }),
          pNoncashItems.map(function (i) { return { label: i.label, amount: i.amount }; }), true);
        allNc.forEach(function (item) { addRow(item.label, item.cur ?? 0, item.prior ?? 0, true); });
      } else {
        noncashItems.forEach(function (item) { addRow(item.label, item.amount, 0, true); });
      }
    }
    if (wcChanges.length > 0) {
      aoa.push(['  Changes in operating assets and liabilities:']);
      wcChanges.forEach(function (item) { addRow(item.label, item.amount, 0, true); });
    }
    const manualOpItems = mergeLabels(rows(state.currentData, 'cf-operating'), multi ? rows(state.priorData, 'cf-operating') : [], multi);
    if (manualOpItems.length > 0) {
      aoa.push(['  Other operating adjustments:']);
      manualOpItems.forEach(function (item) { addRow(item.label, item.cur ?? 0, item.prior ?? 0, true); });
    }
    addRow('Net Cash from Operating Activities', totalOp, pTotalOp, false);

    aoa.push(['Cash Flows from Investing Activities']);
    autoInvItems.forEach(function (item) { addRow(item.label, item.amount, item.priorAmount, true); });
    manualInvItems.forEach(function (item) { addRow(item.label, item.cur ?? 0, item.prior ?? 0, true); });
    addRow('Net Cash from Investing Activities', totalInv, pTotalInv, false);

    aoa.push(['Cash Flows from Financing Activities']);
    autoFinItems.forEach(function (item) { addRow(item.label, item.amount, item.priorAmount, true); });
    manualFinItems.forEach(function (item) { addRow(item.label, item.cur ?? 0, item.prior ?? 0, true); });
    addRow('Net Cash from Financing Activities', totalFin, pTotalFin, false);

    addRow('Net Change in Cash', netChange, pNetChange, false);

    return aoa;
  }

  function buildEquityData(): any[][] {
    const aoa: any[][] = [];
    const entityEl = document.getElementById('nq-entity-type') as HTMLSelectElement | null;
    const entityType = entityEl ? entityEl.value : '';
    const eqTitle = (entityType && (entityType.indexOf('LLC') >= 0 || entityType.indexOf('Partnership') >= 0))
      ? "Statement of Changes in Members' Equity" : "Statement of Stockholders' Equity";

    aoa.push([m.company]);
    aoa.push([eqTitle]);
    aoa.push([flowPeriod()]);
    aoa.push([]);
    aoa.push(['', 'Amount']);

    const netIncome = computeNetIncome(state.currentData);
    const totalEquity = sum(state.currentData, 'equity') + netIncome;
    const pTotalEquity = multi ? sum(state.priorData, 'equity') : 0;
    const pNetIncome = multi ? computeNetIncome(state.priorData) : 0;
    const pTotalEquityWithNI = pTotalEquity + pNetIncome;

    if (multi) {
      aoa.push(['Prior Period']);
      aoa.push(['  Beginning Balance', 0]);
      aoa.push(['  Net Income', pNetIncome]);
      rows(state.priorData, 'equity').forEach(function (item) {
        if (item.label.toLowerCase().indexOf('retained') === -1) {
          aoa.push(['  ' + item.label, parseFloat(String(item.amount)) || 0]);
        }
      });
      aoa.push(['Ending Balance (Prior Period)', pTotalEquityWithNI]);
      aoa.push([]);
      aoa.push(['Current Period']);
      aoa.push(['  Beginning Balance', pTotalEquityWithNI]);
      aoa.push(['  Net Income', netIncome]);
      rows(state.currentData, 'equity').forEach(function (item) {
        if (item.label.toLowerCase().indexOf('retained') === -1) {
          aoa.push(['  ' + item.label, parseFloat(String(item.amount)) || 0]);
        }
      });
      aoa.push(['Ending Balance (Current Period)', totalEquity]);
    } else {
      aoa.push(['Beginning Balance', pTotalEquityWithNI]);
      aoa.push(['  Net Income', netIncome]);
      rows(state.currentData, 'equity').forEach(function (item) {
        if (item.label.toLowerCase().indexOf('retained') === -1) {
          aoa.push(['  ' + item.label, parseFloat(String(item.amount)) || 0]);
        }
      });
      aoa.push(['Ending Balance', totalEquity]);
    }

    return aoa;
  }

  function buildNotesData(): any[][] {
    const aoa: any[][] = [];
    aoa.push([m.company]);
    aoa.push(['Notes to the Financial Statements']);
    aoa.push([flowPeriod()]);
    aoa.push([]);

    const notesEl = document.getElementById('notes-statement');
    if (notesEl && notesEl.innerText.trim()) {
      const lines = notesEl.innerText.split('\n');
      lines.forEach(function (line: string) {
        aoa.push([line]);
      });
    } else {
      aoa.push(['Notes have not been generated yet.']);
    }
    return aoa;
  }

  // Build sheets (standard financial statement order)
  const bsData = buildBSData();
  const wsBS = XLSX.utils.aoa_to_sheet(bsData);
  wsBS['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, wsBS, 'Balance Sheet');

  const isData = buildISData();
  const wsIS = XLSX.utils.aoa_to_sheet(isData);
  wsIS['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, wsIS, 'Income Statement');

  const eqData = buildEquityData();
  const wsEq = XLSX.utils.aoa_to_sheet(eqData);
  wsEq['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, wsEq, 'Equity');

  const cfData = buildCFData();
  const wsCF = XLSX.utils.aoa_to_sheet(cfData);
  wsCF['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, wsCF, 'Cash Flows');

  const notesData = buildNotesData();
  const wsNotes = XLSX.utils.aoa_to_sheet(notesData);
  wsNotes['!cols'] = [{ wch: 80 }];
  XLSX.utils.book_append_sheet(wb, wsNotes, 'Notes');

  const filename = (m.company || 'Financial_Statements').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_') + '.xlsx';
  XLSX.writeFile(wb, filename);
  } catch (err) {
    console.error('Excel export failed:', err);
    alert('Export failed. Please try again.');
  }
}
